import React from 'react';
import classnames from 'classnames';
import { Card } from 'tea-component';
import GameImage from '@/pages/home/components/GameImage';
import type { BasicInfoHeaderProps, BasicInfoMetaItem } from './types';
import './index.scss';

const COVER_SIZES: Record<string, { width: number; height: number; }> = {
  small: { width: 64, height: 64 },
  medium: { width: 90, height: 90 },
  large: { width: 120, height: 120 },
};

const MetaItem: React.FC<{ item: BasicInfoMetaItem; }> = ({ item }) => {
  if (item.value === null || item.value === undefined || item.value === '') {
    return null;
  }

  return (
    <span className="dbi-basic-info-header__meta-item">
      <span className="dbi-basic-info-header__meta-text">
        {item.value}
      </span>
    </span>
  );
};

/** 加载骨架 — 匹配 header 布局形状 */
const LoadingSkeleton: React.FC<{ coverSize: { width: number; height: number; }; }> = ({
  coverSize,
}) => (
  <div className="dbi-basic-info-header__content">
    <div className="dbi-basic-info-header__left">
      <div
        className="dbi-basic-info-header__skeleton-cover"
        style={{ width: coverSize.width, height: coverSize.height }}
      />
      <div className="dbi-basic-info-header__identity">
        <div className="dbi-basic-info-header__skeleton-line dbi-basic-info-header__skeleton-line--title" />
        <div className="dbi-basic-info-header__skeleton-line dbi-basic-info-header__skeleton-line--meta" />
      </div>
    </div>
  </div>
);

const BasicInfoHeader: React.FC<BasicInfoHeaderProps> = ({
  className,
  isLoading = false,
  cover = '',
  coverAlt = '',
  coverSize = 'medium',
  backgroundImage = '',
  backgroundImageVariant = 'full',
  name = '',
  nameExtra,
  platforms = [],
  metaItems = [],
  actions,
  extra,
}) => {
  const coverDimensions = COVER_SIZES[coverSize] || COVER_SIZES.medium;
  const bgClass = backgroundImageVariant === 'right-half'
    ? 'dbi-basic-info-header__bg--right-half'
    : 'dbi-basic-info-header__bg--full';

  if (isLoading) {
    return (
      <Card className={classnames('dbi-basic-info-header', className)}>
        <LoadingSkeleton coverSize={coverDimensions} />
      </Card>
    );
  }

  return (
    <Card className={classnames('dbi-basic-info-header', className)}>
      {backgroundImage && (
        <div className={classnames('dbi-basic-info-header__bg', bgClass)}>
          <div
            className="dbi-basic-info-header__bg-img"
            style={{ backgroundImage: `url(${backgroundImage})` }}
          />
        </div>
      )}
      <div className="dbi-basic-info-header__content">
        <div className="dbi-basic-info-header__left">
          <GameImage
            src={cover}
            className="dbi-basic-info-header__cover"
            alt={coverAlt}
            style={{
              width: coverDimensions.width,
              height: coverDimensions.height,
            }}
          />
          <div className="dbi-basic-info-header__identity">
            <div className="dbi-basic-info-header__identity-title">
              <div className="dbi-basic-info-header__name">
                {name || '-'}
              </div>
              {platforms.length > 0 && (
                <div className="dbi-basic-info-header__platforms">
                  {platforms.map((platform, index) => (
                    <img
                      key={platform.key || `${platform.alt}-${index}`}
                      src={platform.icon}
                      alt={platform.alt}
                    />
                  ))}
                </div>
              )}
              {nameExtra}
            </div>
            <div className="dbi-basic-info-header__meta">
              {metaItems.length > 0 ? (
                metaItems.map((item) => (
                  <MetaItem key={item.key} item={item} />
                ))
              ) : (
                <span className="dbi-basic-info-header__meta-placeholder">-</span>
              )}
            </div>
          </div>
        </div>
        {extra && (
          <div className="dbi-basic-info-header__extra">{extra}</div>
        )}
        {actions && (
          <div className="dbi-basic-info-header__actions">{actions}</div>
        )}
      </div>
    </Card>
  );
};

export default BasicInfoHeader;
